import React, { useEffect, useState } from 'react';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { BaseProductAssignDialogProps } from '../Product.interface';
import { useFindProductHazards } from '../../hazards/Hazard.hooks';
import { Hazard } from '../../hazards/Hazard.interface';
import { getHazardIdPrefix } from '../../hazards/Hazard.config';

export interface AssignHazardDialogProps
  extends Omit<BaseProductAssignDialogProps<never, Hazard>, 'onAssigned'> {
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (hazards: Hazard[]) => void;
}

const AssignHazardDialog: React.FC<AssignHazardDialogProps> = props => {
  const {
    open,
    productId,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = `Assign Hazards`,
    contentText = `Search for Hazards to assign.`,
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);

  const {
    productHazardList,
    isLoadingProductHazards,
    loadingProductHazardsError,
    findProductHazards
  } = useFindProductHazards(productId);

  // handle searching
  useEffect(() => {
    findProductHazards({ search, page, limit });
  }, [search, limit, page, findProductHazards]);

  useEffect(() => {
    if (loadingProductHazardsError) {
      onError?.(loadingProductHazardsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductHazardsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label='Hazard'
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: item => `${getHazardIdPrefix()}-${item.id}`
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: item => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductHazards || isLoading}
          dataList={productHazardList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductHazardsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignHazardDialog;
